/**
 * Series detail — the only screen that shows seasons and episodes.
 *
 * Browse endpoints never return them, so this is the sole entry point into a series'
 * children, and the only place `/watch/:id` is reachable for an episode.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { SeriesDetailDto } from '@filmodasi/shared';
import { useApi } from '../api/hooks';
import { FavoriteButton } from '../components/FavoriteButton';
import { RecommendButton } from '../components/RecommendButton';
import { Icon } from '../components/Icon';
import { ErrorBox, Empty, Spinner } from '../components/ui';
import { resumeEpisode, watchedPercent } from '../catalog/episodeProgress';
import { seriesCounts } from '../catalog/seriesCounts';

export function SeriesDetailPage() {
  const { id } = useParams();
  const { data, error, loading, reload } = useApi<SeriesDetailDto>(
    id ? `/api/catalog/series/detail/${id}` : null,
  );
  const [season, setSeason] = useState<number | null>(null);

  /* Computed over the whole series, never over the visible season — see `resumeEpisode`. */
  const resume = useMemo(
    () => (data ? resumeEpisode(data.episodes, data.progress) : null),
    [data],
  );

  /*
   * The season somebody is actually watching, not season 1.
   *
   * `season` is null until a tab is clicked, so the resume point picks the opening tab and
   * then stops mattering — a viewer who has deliberately gone to look at season 1 is not
   * dragged back to season 4 when the next progress report lands.
   */
  const activeSeason =
    season ?? resume?.seasonNumber ?? data?.seasons[0]?.seasonNumber ?? 1;
  const episodes = useMemo(
    () => data?.episodes.filter((e) => (e.seasonNumber ?? 1) === activeSeason) ?? [],
    [data, activeSeason],
  );

  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;

  const counts = seriesCounts(data.seasons);

  return (
    <main className="detail">
      <header className="detail__header">
        {data.series.poster && <img src={data.series.poster} alt="" className="detail__poster" />}
        <div>
          <h1>{data.series.title}</h1>
          {/* Year and counts on one line: they answer the same question — how much of a
              commitment is this — and two stacked one-item rows read as a form. */}
          {(data.series.year || counts) && (
            <p className="detail__meta">
              {[data.series.year, counts].filter(Boolean).join(' · ')}
            </p>
          )}
          {data.series.genres.length > 0 && (
            <p className="detail__meta">{data.series.genres.join(' · ')}</p>
          )}
          {data.series.plot && <p className="detail__plot">{data.series.plot}</p>}
          {/* Enrichment fills these in over days, so both are routinely absent and each
              is rendered independently rather than as one block that needs both. */}
          {data.series.cast.length > 0 && (
            <p className="detail__meta">
              <span className="detail__label">Oyuncular</span> {data.series.cast.slice(0, 8).join(', ')}
            </p>
          )}
          {data.series.director && (
            <p className="detail__meta">
              <span className="detail__label">Yönetmen</span> {data.series.director}
            </p>
          )}
          <div className="formrow">
            <FavoriteButton item={data.series} />
            {/* The series, not an episode: recommending "S02B07" to somebody who has not
                started the show is not a recommendation. */}
            <RecommendButton itemId={data.series.id} />
          </div>
        </div>
      </header>

      {data.seasons.length === 0 ? (
        <Empty>Bu dizinin bölümleri henüz alınmadı.</Empty>
      ) : (
        <>
          <nav className="rail" aria-label="Sezonlar">
            {data.seasons.map((s) => (
              <button
                key={s.id}
                type="button"
                className={s.seasonNumber === activeSeason ? 'active' : ''}
                onClick={() => setSeason(s.seasonNumber)}
              >
                {s.title} <span className="rail__count">{s.episodeCount}</span>
              </button>
            ))}
          </nav>

          <ul className="episodes">
            {episodes.map((ep) => {
              const row = data.progress[ep.id];
              const percent = watchedPercent(row);
              return (
                <li key={ep.id}>
                  <Link
                    to={`/watch/${ep.id}`}
                    className={ep.id === resume?.id ? 'is-resume' : undefined}
                  >
                    <span className="episodes__num">{ep.episodeNumber ?? '—'}</span>
                    <span className="episodes__title">{ep.title}</span>
                    {/*
                      Its own pill, not `.card__badge`.

                      That class is `position: absolute; inset: auto 0 0 0` — it is the
                      strip across the bottom of a poster, and on a row it covers the
                      title. It looked harmless here only because the row had no
                      positioned ancestor to anchor to; giving the row `position:
                      relative` for the progress strip is what made it land on top of
                      the episode it was describing.
                    */}
                    {!ep.playable && <span className="episodes__badge">Tarayıcıda oynatılamaz</span>}
                    {ep.id === resume?.id && <span className="episodes__resume">Devam et</span>}
                    {/*
                      Titled rather than aria-hidden: the tick is the only thing carrying
                      "watched", so a screen reader that skips it as decoration gets a list
                      of episodes with no state on it at all.
                    */}
                    {row?.completed && (
                      <Icon name="check" size={0.95} title="İzlendi" className="episodes__tick" />
                    )}
                    {/*
                      Drawn for finished episodes too, at full width. A column of full bars
                      is what makes "I have seen down to here" readable while scrolling
                      twenty-four rows, which reading twenty-four ticks is not.
                    */}
                    {percent > 0 && (
                      <span className="episodes__bar" aria-hidden>
                        <span style={{ width: `${percent}%` }} />
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </main>
  );
}
